import { useRef, useState } from 'preact/hooks'
import { Upload, RotateCcw } from 'lucide-preact'
import { PersonAvatar } from '@/components/common/Avatar'
import { websApi } from '@/api/webs'

interface SpeakerHeadshotFieldProps {
  /** Speaker display name, for the avatar initials fallback + alt text. */
  name: string
  /** LinkedIn-enriched photo used as the default when no custom override is set. */
  linkedInPhotoUrl?: string | null
  /** Current custom override URL (null/undefined means the LinkedIn photo is in use). */
  customUrl?: string | null
  /** Set a new override URL, or null to revert to the LinkedIn photo. */
  onChange: (customUrl: string | null) => void
  /**
   * Told when an upload starts and settles. The URL reaches onChange only when the upload returns, so a save
   * made in between would go out without the photo the operator just picked.
   */
  onUploadingChange?: (uploading: boolean) => void
  workspaceId?: string
  webId?: string
  disabled?: boolean
}

const MAX_BYTES = 5 * 1024 * 1024

/**
 * Headshot control for the speaker editor. Shows the custom override when set,
 * otherwise the LinkedIn-enriched photo. Uploading stores a per-event custom
 * image (server: public-images/speaker-headshots/{webId}) that overrides the
 * LinkedIn photo only in this event's speakers area; revert clears it.
 */
export function SpeakerHeadshotField({ name, linkedInPhotoUrl, customUrl, onChange, onUploadingChange, workspaceId, webId, disabled }: SpeakerHeadshotFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const usingCustom = !!customUrl
  const displayUrl = customUrl || linkedInPhotoUrl || null
  const canUpload = !!workspaceId && !!webId && !disabled && !uploading

  const handleFile = async (e: Event) => {
    const target = e.target as HTMLInputElement
    const file = target.files?.[0]
    target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) { setError('Please choose an image file'); return }
    if (file.size > MAX_BYTES) { setError('Image must be under 5MB'); return }
    if (!workspaceId || !webId) return
    setError(null)
    setUploading(true)
    onUploadingChange?.(true)
    try {
      const res = await websApi.uploadSpeakerHeadshot(workspaceId, webId, file)
      if (res.data?.url) onChange(res.data.url)
      else setError(res.error || 'Upload failed')
    } catch {
      setError('Upload failed')
    } finally {
      setUploading(false)
      // Called here rather than from an effect on `uploading`: the field unmounts when the Add panel steps
      // Back mid-upload, and the panel still has to learn the upload settled.
      onUploadingChange?.(false)
    }
  }

  return (
    <div class="flex items-start gap-4">
      <div class="ring-4 ring-white dark:ring-slate-900 rounded-full shrink-0">
        <PersonAvatar fullName={name} profilePicUrl={displayUrl} size="xl" alt={`${name || 'Speaker'} headshot`} />
      </div>
      <div class="min-w-0 flex-1">
        <label class="block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">Headshot</label>
        <input ref={inputRef} type="file" accept="image/*" class="hidden" onChange={handleFile} />
        <div class="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={!canUpload}
            onClick={() => inputRef.current?.click()}
            class="inline-flex items-center gap-2 px-3 py-1.5 text-sm border-2 border-dashed rounded-lg border-slate-300 dark:border-slate-600 hover:border-primary-400 dark:hover:border-primary-500 text-slate-600 dark:text-slate-300 disabled:opacity-50 disabled:pointer-events-none transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {uploading
              ? <div class="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
              : <Upload class="w-4 h-4" />}
            {uploading ? 'Uploading…' : usingCustom ? 'Replace photo' : 'Upload custom photo'}
          </button>
          {usingCustom && (
            <button
              type="button"
              disabled={disabled || uploading}
              onClick={() => { setError(null); onChange(null) }}
              class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <RotateCcw class="w-3.5 h-3.5" /> Use LinkedIn photo
            </button>
          )}
        </div>
        <p class="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
          {usingCustom
            ? 'Custom photo, shown only in this event’s speakers area.'
            : linkedInPhotoUrl
              ? 'Using the LinkedIn photo. Upload to override it here.'
              : 'No LinkedIn photo found. Upload one to show a headshot.'}
        </p>
        {error && <p class="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </div>
  )
}
